import { Injectable, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, throwError, Subscription, timer, of } from 'rxjs';
import { catchError, map, finalize, switchMap, exhaustMap } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { BackendService } from './backend.service';
import { Router } from '@angular/router';
import { constants } from '../constants';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserProfileService } from './data/main/user-profile/user-profile.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseUrl: string = `${environment.baseUrl}/`;
  authController = 'auth/';
  jwtHelper: JwtHelperService = new JwtHelperService();
  private accessTimer?: Subscription;
  private readonly DELAY_TIME = 3 * 1000;

  constructor(
    private backendService: BackendService,
    private userProfileService: UserProfileService,
    private router: Router,
    private storageService: StorageService,
  ) {}

  login(userDetails: any): Observable<any> {
    return this.backendService
      .postWithOptions(`${this.baseUrl}auth/admin/login/`, userDetails, {
        observe: 'response' as 'body',
      })
      .pipe(
        exhaustMap((response) => this.startSpecialSession(response.body.data.tokens)),
        switchMap((userId) => this.getUserDetails(userId)),
        map((userDetails) => {
          this.updateUserStore(userDetails);
          this.startRefreshTokenTimer();
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  activateUser(userDetails: { username: string; password: string; key: string }): Observable<any> {
    return this.backendService
      .postWithOptions(`${this.baseUrl}users/invite/validate/`, userDetails, {
        observe: 'response' as 'body',
      })
      .pipe(
        map((res) => {
          return res;
        }),
      );
  }

  resetPassword(passwordResetInfo: { password: string; uid_base64: string; token: string }): Observable<any> {
    return this.backendService.patch(`${this.baseUrl}${this.authController}password-reset/`, passwordResetInfo).pipe(
      map((res) => {
        return res;
      }),
    );
  }

  register(userDetails: any): Observable<any> {
    return this.backendService.post(`${this.baseUrl}${this.authController}register/`, userDetails).pipe(map((res) => res));
  }

  activeSession() {
    return this.storageService.isKeyExist(constants.LM_AUTH);
  }

  startSpecialSession(encodedToken: any): Observable<any> {
    const { access, refresh, web_socket_auth_token } = encodedToken;
    const decodedUserInfo = this.jwtHelper.decodeToken(access);

    const authData = {
      decodedUserInfo,
      access_token: access,
      refresh_token: refresh,
      web_socket_auth_token,
    };

    this.storageService.setItem(constants.LM_AUTH, { ...authData });
    return of(authData);
  }

  getUserDetails(authData: any) {
    return this.userProfileService.getUserProfile(authData);
  }

  requestResetPassword(email: string) {
    return this.backendService
      .post(`${this.baseUrl}${this.authController}request-password-reset-email/`, { email })
      .pipe(map((res) => res));
  }

  updateUserStore(userDetails: any): void {
    let currentUser = { ...userDetails };
    this.storageService.setItem(constants.LM_USER, currentUser);
  }

  logout(isReturnUrl = false): void {
    const refresh_token = this.storageService.getItem(constants.LM_AUTH)?.refresh_token;
    const data = { refresh_token };
    this.backendService
      .post(`${this.baseUrl}${this.authController}logout/`, data)
      .pipe(
        finalize(() => this.resetSessionParams()),
        catchError((error) => {
          return throwError(error);
        }),
      )
      .subscribe(() => {
        const returnUrl = this.router.url;
        const params = isReturnUrl && ![constants.HOME_ROUTE, '404', '/'].includes(returnUrl) ? { queryParams: { returnUrl } } : undefined;
        this.router.navigate([constants.DEFAULT_ROUTE], params);
      });
  }

  clearSessionStorage() {
    this.storageService.removeItem(constants.LM_AUTH);
    this.storageService.removeItem(constants.LM_USER);
  }

  private stopRefreshTokenTimer(): void {
    if (this.accessTimer) {
      this.accessTimer.unsubscribe();
    }
  }

  private resetSessionParams(): void {
    this.stopRefreshTokenTimer();
    this.clearSessionStorage();
  }

  private startRefreshTokenTimer(): void {
    const tokenLifespan = 300;

    this.accessTimer = timer(tokenLifespan * 1000 - this.DELAY_TIME).subscribe({
      next: () => {
        this.refreshToken().subscribe();
      },
    });
  }

  public refreshToken(): Observable<void | unknown | any> {
    const refresh_token = this.storageService.getItem(constants.LM_AUTH)?.refresh_token;
    const data = { refresh_token };
    return this.backendService.post(`${this.baseUrl}${this.authController}refresh/token/`, data).pipe(
      map((res) => {
        if (!this.activeSession()) {
          this.resetSessionParams();
          return;
        }
        this.startSpecialSession(res?.data?.tokens);
      }),
      catchError((errorRes) => {
        return errorRes;
      }),
    );
  }
}
